import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";
import { ANALYTICS_EVENTS } from "../../../../../../analytics-store";
import { APPS } from "../../../../../../constants/AppsConstants";
import { IRootStore } from "../../../../../../mobx/root-store";
import { ImageToImageConfigType, PlaygroundImage, PlaygroundResult } from "../../../../../../models/image-to-image";
import QueryService from "../../../../../../utils/QueryService";
import { IImageToImageStore } from "../../../image-to-image-store";

export interface IStyleStore {
	mixFaceBody: () => Promise<void>;
	refineColor: () => Promise<void>;
	nflRmbgBeforeStyle: (images: PlaygroundImage[]) => Promise<void>;
}

export class StyleStore implements IStyleStore {
	rootStore: IRootStore;
	imageToImageStore: IImageToImageStore;
	private queryService: QueryService = new QueryService("/image-to-image");

	constructor(rootStore: IRootStore, imageToImageStore: IImageToImageStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
		this.imageToImageStore = imageToImageStore;
	}

	mixFaceBody = async (): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const selectedImages: PlaygroundImage[] = playgroundStore.getSelectedImages();
		const isMixEnabled =
			selectedImages.length === 2 &&
			selectedImages.every(
				(image) =>
					image.type === APPS.IMAGE_TO_IMAGE &&
					(image.config as ImageToImageConfigType)?.original_image.id ===
						(selectedImages[0].config as ImageToImageConfigType)?.original_image.id,
			);
		if (!isMixEnabled) return;
		const savedIndex = playgroundStore.playgroundResults.length;
		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...this.imageToImageStore.config, num_results: 1 },
			type: APPS.IMAGE_TO_IMAGE,
			images: Array.from({ length: 2 }).map((_) => ({
				id: "",
				url: "",
				loading: true,
				playground_result_id: "",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		Array.from({ length: 2 }).forEach(async (_, index) => {
			try {
				const formData = new FormData();
				this.imageToImageStore.handleConfigChange(
					"original_image",
					(selectedImages[index].config as ImageToImageConfigType).original_image,
				);
				const faceImage = selectedImages[index % 2];
				const bodyImage = selectedImages[(index + 1) % 2];
				formData.append(
					"config",
					JSON.stringify({
						...this.imageToImageStore.config,
						num_results: 1,
						style: {
							...this.imageToImageStore.config.style,
							face_seed: faceImage.style_props?.face_seed,
							face_sd_edit: faceImage.style_props?.face_sd_edit,
							face_mask_steps: faceImage.style_props?.face_mask_steps,
							num_inference_steps: faceImage.style_props?.num_inference_steps,
							canny_scale: faceImage.style_props?.canny_scale,
							body_seed: bodyImage.style_props?.body_seed,
							body_sd_edit: bodyImage.style_props?.body_sd_edit,
							logo_enhance: bodyImage.style_props?.logo_enhance,
							box_threshold: bodyImage.style_props?.box_threshold,
							text_threshold: bodyImage.style_props?.text_threshold,
							max_size_height: bodyImage.style_props?.max_size_height,
							max_size_width: bodyImage.style_props?.max_size_width,
							min_size: bodyImage.style_props?.min_size,
						},
					}),
				);
				const { results } = await this.queryService.post("/", formData, {
					"Content-Type": "multipart/form-data",
				});

				runInAction(() => {
					const startIndex = index;
					results.forEach((generatedImage: PlaygroundImage, index: number) => {
						playgroundStore.playgroundResults[savedIndex].images[startIndex + index] = {
							...generatedImage,
							id: uuidv4(),
							loading: true,
							config: {
								...playgroundStore.playgroundResults[savedIndex].config,
								original_image: (selectedImages[index].config as ImageToImageConfigType).original_image,
							} as ImageToImageConfigType,
							type: APPS.IMAGE_TO_IMAGE,
							sessionId: selectedImages[index].sessionId,
						};
						this.rootStore.analyticsStore.logImageToImageEvent(
							ANALYTICS_EVENTS.ITI_MIX_FACE_BODY,
							playgroundStore.playgroundResults[savedIndex].images[startIndex + index],
						);
					});
				});
			} catch (e: any) {
				this.imageToImageStore.showAPIError(e);
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_: PlaygroundResult, index: number) => index !== savedIndex,
					);
					throw new Error(`Error generating images: ${e.message || e.toString()}`);
				});
			}
		});
	};

	refineColor = async (): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const selectedImages: PlaygroundImage[] = playgroundStore.getSelectedImages();
		if (!selectedImages.length) return;
		const savedIndex = playgroundStore.playgroundResults.length;
		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config: { ...this.imageToImageStore.config, num_results: 1 },
			type: APPS.IMAGE_TO_IMAGE,
			images: Array.from({ length: selectedImages.length }).map((_) => ({
				id: "",
				url: "",
				loading: true,
				playground_result_id: "",
			})),
		};
		playgroundStore.playgroundResults = [...playgroundStore.playgroundResults, resultsSkeletons];
		Array.from({ length: selectedImages.length }).forEach(async (_, index) => {
			try {
				const formData = new FormData();
				this.imageToImageStore.handleConfigChange(
					"original_image",
					(selectedImages[index].config as ImageToImageConfigType).original_image,
				);
				formData.append(
					"config",
					JSON.stringify({
						...this.imageToImageStore.config,
						num_results: 1,
						style: {
							...this.imageToImageStore.config.style,
							is_normalize_face: true,
							face_seed: selectedImages[index].style_props?.face_seed,
							face_sd_edit: selectedImages[index].style_props?.face_sd_edit,
							num_inference_steps: selectedImages[index].style_props?.num_inference_steps,
							face_mask_steps: selectedImages[index].style_props?.face_mask_steps,
							canny_scale: selectedImages[index].style_props?.canny_scale,
							body_seed: selectedImages[index].style_props?.body_seed,
							body_sd_edit: selectedImages[index].style_props?.body_sd_edit,
							logo_enhance: selectedImages[index].style_props?.logo_enhance,
							box_threshold: selectedImages[index].style_props?.box_threshold,
							text_threshold: selectedImages[index].style_props?.text_threshold,
							max_size_height: selectedImages[index].style_props?.max_size_height,
							max_size_width: selectedImages[index].style_props?.max_size_width,
							min_size: selectedImages[index].style_props?.min_size,
						},
					}),
				);
				const { results } = await this.queryService.post("/", formData, {
					"Content-Type": "multipart/form-data",
				});

				runInAction(() => {
					const startIndex = index;
					results.forEach((generatedImage: PlaygroundImage, index: number) => {
						playgroundStore.playgroundResults[savedIndex].images[startIndex + index] = {
							...generatedImage,
							id: uuidv4(),
							loading: true,
							config: {
								...playgroundStore.playgroundResults[savedIndex].config,
								original_image: (selectedImages[index].config as ImageToImageConfigType).original_image,
							} as ImageToImageConfigType,
							type: APPS.IMAGE_TO_IMAGE,
						};
						this.rootStore.analyticsStore.logImageToImageEvent(
							ANALYTICS_EVENTS.ITI_COLOR_REFINE_IMAGE,
							playgroundStore.playgroundResults[savedIndex].images[startIndex + index],
						);
					});
				});
			} catch (e: any) {
				this.imageToImageStore.showAPIError(e);
				runInAction(() => {
					playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
						(_: PlaygroundResult, index: number) => index !== savedIndex,
					);
					throw new Error(`Error generating images: ${e.message || e.toString()}`);
				});
			}
		});
	};

	nflRmbgBeforeStyle = async (images: PlaygroundImage[]): Promise<void> => {
		// Related to styles (isFox)
		await this.imageToImageStore.generateImageToImage(
			images,
			{
				original_image: { id: "", url: "", playground_result_id: "" },
				background: { remove: true, is_fox: true },
				num_results: 1,
			},
			true,
			undefined,
			true, // Skip tracking for internal operations
		);
	};
}
